import type { StockItemLocationDto, StorageLocationDto } from '@nest/domain-entities-metadata-generated';
import { Table, TableBody, TableCell, TableHead, TableRow, Tabs, TabsContent, TabsList, TabsTrigger, useValidationResultDialog } from '@nest/uikit-react';
import { useQuery } from '@tanstack/react-query';
import { SquareDashedIcon } from 'lucide-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router';
import { useTerminalClient } from '../client-rpc-router';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';

export default function StorageLocationDetailPage() {
	const client = useTerminalClient();
	const id = useNumberParam('id');
	const [searchParams] = useSearchParams();
	const code = searchParams.get('code');

	const [activeTab, setActiveTab] = useState('default');
	const [bottomElementRef, setBottomElementRef] = useState<HTMLDivElement | null>(null);

	const { data, error } = useQuery<StorageLocationDto | null>({
		queryKey: ['storage-location', id, code],
		queryFn: async () => {
			if (code) {
				return client.wms.findStorageLocationByCodeQuery.execute({ code });
			}
			return null;
		},
		enabled: Boolean(code)
	});

	const { data: items, error: itemsError } = useQuery<StockItemLocationDto[] | undefined>({
		queryKey: ['storage-location-items', data?.id, activeTab],
		queryFn: () => {
			const wmsExtra = client.wms as unknown as {
				getStorageLocationStockItemLocationsQuery: {
					execute: (input: { id: number }) => Promise<StockItemLocationDto[]>;
				};
			};

			return wmsExtra.getStorageLocationStockItemLocationsQuery.execute({ id: data!.id });
		},
		enabled: activeTab === 'items' && Boolean(data?.id)
	});

	useValidationResultDialog([error, itemsError]);

	return (
		<TerminalScreen showBackButton topBar={<div>{data?.name}</div>} bottomContent={<div ref={setBottomElementRef} />}>
			<Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
				<TabsContent value="default" className="h-full">
					<Table>
						<TableBody>
							<TableRow>
								<TableHead className="w-1/2">Kód</TableHead>
								<TableCell className="w-1/2">{data?.code ?? code}</TableCell>
							</TableRow>
							{data?.name && (
								<TableRow>
									<TableHead className="w-1/2">Název</TableHead>
									<TableCell className="w-1/2">{data.name}</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TabsContent>
				<TabsContent value="items" className="h-full">
					<div className="flex flex-col divide-y rounded-md border gap-2">
						{items?.map(loc => (
							<div key={loc.id} className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-muted/50">
								<div className="min-w-0">
									<div className="font-medium">{loc.stockItem.item.name}</div>
									<div className="text-muted-foreground">{loc.stockItem.item.code}</div>
									{loc.handlingUnit?.code && (
										<div className="flex items-center gap-1">
											<SquareDashedIcon className="w-4 h-4" />
											{loc.handlingUnit.code}
										</div>
									)}
								</div>
								<div className="ml-3 shrink-0 text-right">
									<div className="rounded bg-muted px-2 py-1 font-semibold">{loc.quantity} ks</div>
								</div>
							</div>
						))}
					</div>
				</TabsContent>

				{bottomElementRef &&
					createPortal(
						<TabsList className="mt-auto w-full">
							<TabsTrigger value="default" className="w-full">
								<span>Základní</span>
							</TabsTrigger>
							<TabsTrigger value="items" className="w-full">
								<span>Zboží</span>
							</TabsTrigger>
						</TabsList>,
						bottomElementRef
					)}
			</Tabs>
		</TerminalScreen>
	);
}
